/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Dropdown from "react-bootstrap/Dropdown";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import InputGroup from "react-bootstrap/InputGroup";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import ListGroup from "react-bootstrap/ListGroup";
import { FaPencilAlt, FaTooth, FaCopy } from "react-icons/fa";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from 'react-toastify';

// Styles (unchanged)
const modalBodyStyle = {
  padding: "20px",
};

const toothButtonStyle = {
  width: "30px",
  height: "40px",
  margin: "2px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "2px solid #ccc",
  borderRadius: "4px",
  fontWeight: "bold",
  fontSize: "14px",
  padding: 0,
};

const selectedToothStyle = {
  ...toothButtonStyle,
  backgroundColor: "#004aad",
  color: "white",
  borderColor: "#004aad",
};

const requiredLabelStyle = {
  display: "block",
  marginBottom: "5px",
  fontWeight: "bold",
  color: "#000000",
};

// ProcedureSelectorModal Component (unchanged)
const ProcedureSelectorModal = ({
  show,
  handleClose,
  onSelect,
  allProcedures,
  accountId,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const frequentlyUsedProcedureIds = ["1093", "1035"];

  const filteredProcedures = useMemo(() => {
    if (!searchTerm) {
      return allProcedures.filter((proc) => !accountId || proc.accountId === accountId);
    }
    return allProcedures.filter(
      (proc) =>
        proc.label.toLowerCase().includes(searchTerm.toLowerCase()) &&
        (!accountId || proc.accountId === accountId)
    );
  }, [searchTerm, allProcedures, accountId]);

  const frequentlyUsed = filteredProcedures.filter((p) =>
    frequentlyUsedProcedureIds.includes(p.procedureId)
  );
  const generalProcedures = filteredProcedures.filter(
    (p) => !frequentlyUsedProcedureIds.includes(p.procedureId) && p.value
  );

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header
        closeButton
        style={{
          backgroundColor: "#004aad",
          color: "white",
          borderBottom: "none",
        }}
      >
        <Modal.Title>Select Procedure</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: "0" }}>
        <div style={{ padding: "1rem", borderBottom: "1px solid #dee2e6" }}>
          <Form.Control
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div style={{ maxHeight: "60vh", overflowY: "auto", padding: "1rem" }}>
          {frequentlyUsed.length > 0 && (
            <>
              <h5>Frequently Used</h5>
              <ListGroup variant="flush">
                {frequentlyUsed.map((proc) => (
                  <ListGroup.Item
                    key={`freq-${proc.value}`}
                    action
                    onClick={() => onSelect(proc.value)}
                    style={{
                      cursor: "pointer",
                      border: "none",
                      paddingLeft: "0",
                    }}
                  >
                    {proc.label} (${proc.price || 0})
                  </ListGroup.Item>
                ))}
              </ListGroup>
              <hr />
            </>
          )}
          {generalProcedures.length > 0 && (
            <>
              <h5>General Procedures</h5>
              <ListGroup variant="flush">
                {generalProcedures.map((proc) => (
                  <ListGroup.Item
                    key={`gen-${proc.value}`}
                    action
                    onClick={() => onSelect(proc.value)}
                    style={{
                      cursor: "pointer",
                      border: "none",
                      paddingLeft: "0",
                    }}
                  >
                    {proc.label}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </>
          )}
          {frequentlyUsed.length === 0 && generalProcedures.length === 0 && (
            <p>No procedures available for this account.</p>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

// ToothSelectorModal Component (unchanged)
const ToothSelectorModal = ({ show, handleClose, currentTeeth, onSave }) => {
  const [selectedTeeth, setSelectedTeeth] = useState([]);

  useEffect(() => {
    if (currentTeeth) {
      setSelectedTeeth(
        currentTeeth
          .split(",")
          .map((t) => t.trim())
          .filter((t) => t)
      );
    }
  }, [currentTeeth]);

  const toggleTooth = (toothNumber) => {
    setSelectedTeeth((prevSelected) =>
      prevSelected.includes(toothNumber.toString())
        ? prevSelected.filter((t) => t !== toothNumber.toString())
        : [...prevSelected, toothNumber.toString()]
    );
  };

  const toggleGroup = (teethArray) => {
    const teeth = teethArray.map((tooth) => tooth.toString());
    setSelectedTeeth((prevSelected) => {
      const allSelected = teeth.every((tooth) => prevSelected.includes(tooth));
      if (allSelected) {
        return prevSelected.filter((t) => !teeth.includes(t));
      } else {
        const newSelection = [...prevSelected];
        teeth.forEach((tooth) => {
          if (!newSelection.includes(tooth)) {
            newSelection.push(tooth);
          }
        });
        return newSelection;
      }
    });
  };

  const handleDone = () => {
    onSave(
      selectedTeeth.sort((a, b) => parseInt(a) - parseInt(b)).join(", ")
    );
    handleClose();
  };

  const renderToothButton = (toothNumber) => (
    <button
      key={toothNumber}
      onClick={() => toggleTooth(toothNumber)}
      className="tooth-button"
      style={
        selectedTeeth.includes(toothNumber.toString())
          ? selectedToothStyle
          : toothButtonStyle
      }
    >
      {toothNumber}
    </button>
  );

  const teethLayout = {
    upperRight: [18, 17, 16, 15, 14, 13, 12, 11],
    upperLeft: [21, 22, 23, 24, 25, 26, 27, 28],
    lowerRight: [48, 47, 46, 45, 44, 43, 42, 41],
    lowerLeft: [31, 32, 33, 34, 35, 36, 37, 38],
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header
        closeButton
        style={{ backgroundColor: "#004aad", color: "white" }}
      >
        <Modal.Title>Tooth Selector</Modal.Title>
      </Modal.Header>
      <Modal.Body style={modalBodyStyle}>
        <div className="text-center mb-3">
          <h5
            style={{ cursor: "pointer", display: "inline-block" }}
            onClick={() =>
              toggleGroup([...teethLayout.upperRight, ...teethLayout.upperLeft])
            }
          >
            Upper Arch
          </h5>
        </div>
        <div className="mb-4">
          <table className="table table-bordered text-center mb-2">
            <thead>
              <tr>
                <th
                  colSpan="8"
                  className="bg-light"
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleGroup(teethLayout.upperRight)}
                >
                  Upper Right
                </th>
                <th
                  colSpan="8"
                  className="bg-light"
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleGroup(teethLayout.upperLeft)}
                >
                  Upper Left
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                {teethLayout.upperRight.map((tooth) => (
                  <td key={tooth} style={{ padding: "2px" }}>
                    {renderToothButton(tooth)}
                  </td>
                ))}
                {teethLayout.upperLeft.map((tooth) => (
                  <td key={tooth} style={{ padding: "2px" }}>
                    {renderToothButton(tooth)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        <hr />
        <div className="text-center mb-3">
          <h5
            style={{ cursor: "pointer", display: "inline-block" }}
            onClick={() =>
              toggleGroup([...teethLayout.lowerRight, ...teethLayout.lowerLeft])
            }
          >
            Lower Arch
          </h5>
        </div>
        <div>
          <table className="table table-bordered text-center mb-2">
            <thead>
              <tr>
                <th
                  colSpan="8"
                  className="bg-light"
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleGroup(teethLayout.lowerRight)}
                >
                  Lower Right
                </th>
                <th
                  colSpan="8"
                  className="bg-light"
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleGroup(teethLayout.lowerLeft)}
                >
                  Lower Left
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                {teethLayout.lowerRight.map((tooth) => (
                  <td key={tooth} style={{ padding: "2px" }}>
                    {renderToothButton(tooth)}
                  </td>
                ))}
                {teethLayout.lowerLeft.map((tooth) => (
                  <td key={tooth} style={{ padding: "2px" }}>
                    {renderToothButton(tooth)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          onClick={handleDone}
          style={{
            backgroundColor: "#004aad",
            borderColor: "#004aad",
            color: "white",
          }}
        >
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Helper function to get userLabId from localStorage
const getUserLabId = () => {
  const userLabId = localStorage.getItem('userLabId') || localStorage.getItem('labId');
  console.log('Retrieved userLabId from localStorage:', userLabId);
  return userLabId;
};

// ProcedureModel Component (FIXED)
const ProcedureModel = ({ onProcedureAdded, account }) => {
  const { caseId } = useParams();
  const [show, setShow] = useState(false);
  const [selectedProcedure, setSelectedProcedure] = useState({
    label: "Select Procedure",
    value: "",
    price: 0,
    procedureId: "",
    accountId: "",
    unitCount: "Count by Teeth",
  });
  const [teeth, setTeeth] = useState("");
  const [shade, setShade] = useState("");
  const [alloyMaterial, setAlloyMaterial] = useState("");
  const [contactsEmbrasures, setContactsEmbrasures] = useState("");
  const [occlusalContact, setOcclusalContact] = useState("");
  const [insufficientRoom, setInsufficientRoom] = useState("");
  const [retention, setRetention] = useState("");
  const [marginPosition, setMarginPosition] = useState("");
  const [showToothSelectorModal, setShowToothSelectorModal] = useState(false);
  const [showProcedureSelectorModal, setShowProcedureSelectorModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [procedureOptions, setProcedureOptions] = useState([]);
  const [isLoadingProcedures, setIsLoadingProcedures] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [caseAccountId, setCaseAccountId] = useState(null);

  // Fetch case details
  useEffect(() => {
    const fetchCaseDetails = async () => {
      if (!caseId) {
        console.error("No case ID provided");
        toast.error("No case ID provided");
        setIsLoadingProcedures(false);
        return;
      }
      try {
        const response = await axios.get(`https://hms.backend.hiragroupcloud.com/api/cases/cases/${caseId}`, {
          withCredentials: true,
        });
        const caseData = response.data;
        setCaseAccountId(caseData.account_holder || account || null);
      } catch (error) {
        console.error("Error fetching case details:", error.response?.data || error.message);
        setFetchError(`Failed to fetch case details: ${error.message}`);
        toast.error(`Failed to fetch case details: ${error.message}`);
        setCaseAccountId(account || null);
      }
    };
    fetchCaseDetails();
  }, [caseId, account]);

  // Fetch procedures with userLabId
  useEffect(() => {
    const fetchProcedures = async () => {
      if (!caseAccountId) {
        setIsLoadingProcedures(false);
        return;
      }

      // Get userLabId from localStorage
      const userLabId = getUserLabId();
      
      if (!userLabId) {
        console.error("No userLabId found in localStorage");
        setFetchError("User Lab ID not found. Please login again.");
        setIsLoadingProcedures(false);
        toast.error("User Lab ID not found. Please login again.");
        return;
      }

      try {
        setIsLoadingProcedures(true);
        setFetchError(null);

        // IMPORTANT FIX: Include both accountId AND userLabId in the query string
        const url = `https://hms.backend.hiragroupcloud.com/api/procedures/account-specific?accountId=${caseAccountId}&userLabId=${userLabId}`;
        console.log("Fetching procedures from URL:", url);

        const response = await axios.get(url, { withCredentials: true });
        console.log("API Response:", response.data);
        
        const data = response.data.data || [];

        const formattedProcedures = data.map((proc) => ({
          label: `${proc.name} (ID: ${proc.procedureId || 'N/A'})`,
          value: proc._id,
          price: proc.price || 0,
          procedureId: proc.procedureId || '',
          accountId: proc.accountId || caseAccountId,
          unitCount: proc.unitCount || 'Count by Teeth',
        }));

        setProcedureOptions([
          { label: "Select a procedure...", value: "", price: 0, procedureId: "", accountId: "", unitCount: "Count by Teeth" },
          ...formattedProcedures,
        ]);

        if (formattedProcedures.length === 0) {
          toast.warn("No procedures found for this account");
        }
      } catch (error) {
        console.error("Error fetching procedures:", error);
        console.error("Error response:", error.response?.data);
        setFetchError(`Failed to load procedures: ${error.response?.data?.message || error.message}`);
        toast.error(`Failed to load procedures: ${error.response?.data?.message || error.message}`);
        setProcedureOptions([{ label: "Select a procedure...", value: "", price: 0, procedureId: "", accountId: "", unitCount: "Count by Teeth" }]);
      } finally {
        setIsLoadingProcedures(false);
      }
    };

    if (show && caseAccountId) {
      fetchProcedures();
    }
  }, [caseAccountId, show]);

  const handleClose = () => {
    setShow(false);
    resetAllProcedureFields();
  };

  const resetAllProcedureFields = () => {
    setSelectedProcedure({ label: "Select Procedure", value: "", price: 0, procedureId: "", accountId: "", unitCount: "Count by Teeth" });
    setTeeth("");
    setShade("");
    setAlloyMaterial("");
    setContactsEmbrasures("");
    setOcclusalContact("");
    setInsufficientRoom("");
    setRetention("");
    setMarginPosition("");
  };

  const handleProcedureSelect = (procedureId) => {
    const selectedProc = procedureOptions.find((proc) => proc.value === procedureId);
    if (selectedProc) {
      setSelectedProcedure({
        label: selectedProc.label,
        value: selectedProc.value,
        price: selectedProc.price || 0,
        procedureId: selectedProc.procedureId,
        accountId: selectedProc.accountId,
        unitCount: selectedProc.unitCount || "Count by Teeth",
      });
    } else {
      setSelectedProcedure({ label: "Select Procedure", value: "", price: 0, procedureId: "", accountId: "", unitCount: "Count by Teeth" });
    }
    setShowProcedureSelectorModal(false);
  };

  const handleSaveTeeth = (selectedTeethValue) => {
    setTeeth(selectedTeethValue);
    setShowToothSelectorModal(false);
  };

  const handleSaveProcedure = async () => {
    if (!caseId || !selectedProcedure.value || !teeth?.trim() || !shade?.trim()) {
      toast.error("Procedure, teeth, and shade are required");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        customProcedureId: selectedProcedure.procedureId,
        teeth: teeth.trim(),
        shade: shade.trim(),
        alloyMaterial: alloyMaterial || "",
        contactsEmbrasures: contactsEmbrasures || "",
        occlusalContact: occlusalContact || "",
        insufficientRoom: insufficientRoom || "",
        retention: retention || "",
        marginPosition: marginPosition || "",
        price: selectedProcedure.price,
        unitCount: selectedProcedure.unitCount
      };

      console.log("Saving procedure with payload:", payload);

      const response = await axios.post(
        `https://hms.backend.hiragroupcloud.com/api/cases/cases/${caseId}/procedures`,
        payload,
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success(`Procedure added successfully!`);
        resetAllProcedureFields();
        handleClose();
        if (onProcedureAdded) await onProcedureAdded();
      } else {
        toast.error(response.data.message || "Failed to add procedure");
      }
    } catch (error) {
      console.error("Error saving procedure:", error);
      toast.error(error.response?.data?.message || "Failed to add procedure");
    } finally {
      setIsSubmitting(false);
    }
  };

  const alloyOptions = [
    "",
    "Zirconia",
    "Emax",
    "PFM",
    "Gold",
    "Composite",
  ];
  const contactOptions = [
    "",
    "Normal",
    "Broad",
    "Open",
    "Closed",
    "Light",
  ];
  const occlusalOptions = [
    "",
    "In Occlusion",
    "Out of Occlusion",
    "Light Contact",
  ];
  const roomOptions = [
    "",
    "Yes",
    "No",
    "Minimal Prep Required",
  ];
  const retentionOptions = [
    "",
    "Good",
    "Fair",
    "Poor",
    "Mechanical",
    "Adhesive",
  ];
  const marginOptions = [
    "",
    "Supragingival",
    "Equigingival",
    "Subgingival",
  ];

  return (
    <>
      <Dropdown as={ButtonGroup}>
        <Button
          style={{
            backgroundColor: "white",
            borderColor: "white",
            color: "black",
          }}
          onClick={() => setShow(true)}
        >
          <FaPencilAlt style={{ fontSize: "1.2rem", color: "black" }} /> Add
          Procedure
        </Button>
        <Dropdown.Toggle
          split
          style={{
            backgroundColor: "white",
            borderColor: "white",
            color: "black",
          }}
          id="dropdown-split-basic"
        />
        <Dropdown.Menu>
          <Dropdown.Item
            onClick={() => toast.error("Delete Selected Procedure action not implemented")}
          >
            Delete Selected Procedure
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>

      <Modal
        show={show}
        onHide={handleClose}
        size="xl"
        centered
        dialogClassName="custom-modal"
      >
        <Modal.Header
          closeButton
          style={{
            backgroundColor: "#004aad",
            borderColor: "#004aad",
            color: "white",
          }}
        >
          <Modal.Title className="heading">Add Procedure</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ overflowY: "auto", height: "600px" }}>
          <Form>
            <Form.Group as={Col} className="mb-3" controlId="formWork">
              <Form.Label style={requiredLabelStyle}>
                Procedure <span style={{ color: "red" }}>*</span>
              </Form.Label>
              <Col sm={10}>
                {isLoadingProcedures ? (
                  <div className="text-muted">Loading procedures...</div>
                ) : fetchError ? (
                  <div className="text-danger">
                    Error: {fetchError}
                  </div>
                ) : procedureOptions.length <= 1 ? (
                  <div className="text-muted">No procedures available for account ID: {caseAccountId || 'N/A'}</div>
                ) : (
                  <InputGroup style={{ width: "100%", marginLeft: "20%" }}>
                    <Form.Select
                      value={selectedProcedure.value}
                      onChange={(e) => handleProcedureSelect(e.target.value)}
                      disabled={isLoadingProcedures}
                      style={{
                        cursor: "pointer",
                        backgroundColor: "#fff",
                        borderColor: "#f4f4f4",
                      }}
                    >
                      {procedureOptions.map((option, idx) => (
                        <option key={idx} value={option.value}>
                          {option.label.replace(/\(ID:.*\)/, '').trim()}
                        </option>
                      ))}
                    </Form.Select>

                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowProcedureSelectorModal(true)}
                      style={{
                        backgroundColor: "#004aad",
                        borderColor: "#004aad",
                        color: "#fff",
                      }}
                      disabled={isLoadingProcedures || procedureOptions.length <= 1}
                    >
                      <FaCopy style={{ color: "#fff" }} />
                    </Button>
                  </InputGroup>
                )}
              </Col>
            </Form.Group>

            {selectedProcedure.value && (
              <>
                <Form.Group as={Row} className="mb-3" controlId="formTeeth">
                  <Form.Label
                    column
                    sm={2}
                    style={{
                      fontWeight: "600",
                      fontSize: "1rem",
                      color: "#333",
                    }}
                  >
                    Teeth <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <Col sm={10}>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        placeholder="Select Teeth"
                        value={teeth}
                        readOnly
                        onClick={() => setShowToothSelectorModal(true)}
                        style={{
                          cursor: "pointer",
                          borderColor: "#f4f4f4",
                          boxShadow: "0 0 5px rgba(0, 74, 173, 0.3)",
                          borderTopLeftRadius: "8px",
                          borderBottomLeftRadius: "8px",
                          borderTopRightRadius: "0",
                          borderBottomRightRadius: "0",
                        }}
                        required
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={() => setShowToothSelectorModal(true)}
                        style={{
                          backgroundColor: "#004aad",
                          borderColor: "#004aad",
                          borderTopRightRadius: "8px",
                          borderBottomRightRadius: "8px",
                          borderTopLeftRadius: "0",
                          borderBottomLeftRadius: "0",
                        }}
                      >
                        <FaTooth style={{ color: "white" }} />
                      </Button>
                    </InputGroup>
                  </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-3" controlId="formShade">
                  <Form.Label
                    column
                    sm={2}
                    style={{
                      fontWeight: "600",
                      fontSize: "1rem",
                      color: "#333",
                    }}
                  >
                    Shade <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <Col sm={10}>
                    <Form.Control
                      type="text"
                      value={shade}
                      onChange={(e) => setShade(e.target.value)}
                      placeholder="Enter shade"
                      required
                      style={{
                        borderColor: "#f4f4f4",
                        boxShadow: "0 0 5px rgba(0, 74, 173, 0.3)",
                        borderRadius: "8px",
                      }}
                    />
                  </Col>
                </Form.Group>

                {[
                  {
                    id: "formAlloyMaterial",
                    label: "Alloy / Material",
                    value: alloyMaterial,
                    set: setAlloyMaterial,
                    options: alloyOptions,
                  },
                  {
                    id: "formContacts",
                    label: "Contacts / Embrasures",
                    value: contactsEmbrasures,
                    set: setContactsEmbrasures,
                    options: contactOptions,
                  },
                  {
                    id: "formOcclusal",
                    label: "Occlusal Contact",
                    value: occlusalContact,
                    set: setOcclusalContact,
                    options: occlusalOptions,
                  },
                  {
                    id: "formRoom",
                    label: "Insufficient Room",
                    value: insufficientRoom,
                    set: setInsufficientRoom,
                    options: roomOptions,
                  },
                  {
                    id: "formRetention",
                    label: "Retention",
                    value: retention,
                    set: setRetention,
                    options: retentionOptions,
                  },
                  {
                    id: "formMargin",
                    label: "Margin Position",
                    value: marginPosition,
                    set: setMarginPosition,
                    options: marginOptions,
                  },
                ].map((field) => (
                  <Form.Group as={Row} className="mb-3" controlId={field.id} key={field.id}>
                    <Form.Label
                      column
                      sm={2}
                      style={{
                        fontWeight: "600",
                        fontSize: "1rem",
                        color: "#333",
                      }}
                    >
                      {field.label}
                    </Form.Label>
                    <Col sm={10}>
                      <Form.Select
                        value={field.value}
                        onChange={(e) => field.set(e.target.value)}
                        style={{
                          borderColor: "#f4f4f4",
                          boxShadow: "0 0 5px rgba(0, 74, 173, 0.3)",
                          borderRadius: "8px",
                          backgroundColor: "#fff",
                          color: "#333",
                        }}
                      >
                        {field.options.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                  </Form.Group>
                ))}
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: "#f8f9fa" }}>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveProcedure}
            style={{
              backgroundColor: "#004aad",
              borderColor: "#004aad",
              color: "white",
            }}
            disabled={
              !selectedProcedure.value ||
              !teeth ||
              !shade ||
              isSubmitting
            }
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      <ToothSelectorModal
        show={showToothSelectorModal}
        handleClose={() => setShowToothSelectorModal(false)}
        currentTeeth={teeth}
        onSave={handleSaveTeeth}
      />
      <ProcedureSelectorModal
        show={showProcedureSelectorModal}
        handleClose={() => setShowProcedureSelectorModal(false)}
        onSelect={handleProcedureSelect}
        allProcedures={procedureOptions}
        accountId={caseAccountId}
      />
    </>
  );
};

export default ProcedureModel;